import PropTypes from 'prop-types'
import { Button, Tooltip } from 'antd'
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles, useTheme } from 'react-jss'
import dayjs, { formatDOB } from '../../lib/datetime'
import { toPath } from '../../routes'
import DiseasesTags from '../Common/DiseasesTags'
import PatientEntry from '../Common/PatientEntry'

// Width of antd's expand-icon column. The expanded details are indented by the
// same amount so their fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#6b6b6b',
  },
  // The critical marker leads the name so severity reads first.
  criticalIcon: {
    marginRight: 6,
  },
  // Acknowledged state + the row actions share the last line, left-aligned and
  // wrapping so the narrowest phones never clip the Edit button.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    marginTop: 4,
  },
  acknowledged: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: '#595959',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#6b6b6b',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  // Lets a field take the full width of the 2-column details grid.
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

// Same format as the desktop "Created At" column.
function formatCreatedAt(createdAt) {
  if (!createdAt) return null
  return dayjs(createdAt).tz().calendar(null, {
    sameElse: 'MMM D, YYYY h:mm A',
  })
}

export function AlertMobileCard({ alert, fromPracticeParam = '' }) {
  const classes = useStyles()
  const { brandDanger, brandSuccess } = useTheme()
  const { acknowledgedAt, critical, device, id, patientSummary } = alert
  const isAcknowledged = Boolean(acknowledgedAt)
  // Alerts are triaged by when they arrived and on what device.
  const contextLine = [
    formatCreatedAt(alert.createdAt),
    device?.deviceModel?.deviceType?.name,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {critical && (
          <Tooltip title="Requires Attention">
            <ExclamationCircleOutlined
              className={classes.criticalIcon}
              style={{ color: brandDanger }}
            />
          </Tooltip>
        )}
        {patientSummary ? <PatientEntry patient={patientSummary} /> : '—'}
      </div>
      {contextLine && (
        <div className={classes.line}>
          <ClockCircleOutlined className={classes.lineIcon} />
          {contextLine}
        </div>
      )}
      <div className={classes.actionsLine}>
        <span className={classes.acknowledged}>
          {isAcknowledged ? (
            <CheckCircleFilled style={{ color: brandSuccess }} />
          ) : (
            <CloseCircleFilled style={{ color: brandDanger }} />
          )}
          {isAcknowledged ? 'Acknowledged' : 'Not acknowledged'}
        </span>
        <Link to={`${toPath.alerts.alert({ id })}${fromPracticeParam}`}>
          <Button
            type="link"
            size="small"
            icon={<FileSearchOutlined />}
            style={{ paddingLeft: 0 }}
          >
            View
          </Button>
        </Link>
        <Link to={toPath.alerts.alert.edit({ id })}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ paddingLeft: 0 }}
          >
            Edit
          </Button>
        </Link>
      </div>
    </div>
  )
}

AlertMobileCard.propTypes = {
  fromPracticeParam: PropTypes.string,
  alert: PropTypes.shape({
    id: PropTypes.string.isRequired,
    critical: PropTypes.bool,
    createdAt: PropTypes.string,
    acknowledgedAt: PropTypes.string,
    patientSummary: PropTypes.shape({
      id: PropTypes.string,
    }),
    device: PropTypes.shape({
      deviceModel: PropTypes.shape({
        deviceType: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
}

export function AlertMobileDetails({ alert }) {
  const classes = useStyles()
  const { patientSummary } = alert

  return (
    <div className={classes.detailsGrid}>
      <Field label="MRN">{patientSummary?.practiceMrn || '—'}</Field>
      <Field label="DOB">
        {/* formatDOB(undefined) would render today's date. */}
        {patientSummary?.birthDate ? formatDOB(patientSummary.birthDate) : '—'}
      </Field>
      <Field label="Stroke prevention" span>
        <DiseasesTags patientSummary={patientSummary} />
      </Field>
    </div>
  )
}

AlertMobileDetails.propTypes = {
  alert: PropTypes.shape({
    patientSummary: PropTypes.shape({
      practiceMrn: PropTypes.string,
      birthDate: PropTypes.string,
    }),
  }).isRequired,
}
